import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { BdUserSummary } from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import { DateFormat, formatDateToString } from 'us-react-components/dist/utils/date';

function BdUserTooltipContent({ summary }: { summary: BdUserSummary }) {
  const signup = summary.signupDate
    ? formatDateToString(new Date(summary.signupDate * 1000), DateFormat.DATE_FULL_FORMAT)
    : '—';

  return (
    <Box sx={{ p: 0.5, minWidth: 240 }}>
      <Typography variant='caption' sx={{ fontWeight: 700, display: 'block' }}>
        #{summary.id} {summary.fullName ?? '(no name)'}
      </Typography>
      <Typography variant='caption' sx={{ display: 'block' }}>
        {summary.email ?? '(no email)'}
      </Typography>
      <Typography variant='caption' sx={{ display: 'block', color: colors.gray[300] }}>
        Joined: {signup} · {summary.blocked ? 'Blocked' : 'Active'}
      </Typography>
      {summary.latestNotes.length > 0 && (
        <Box
          sx={{
            mt: 0.75,
            pt: 0.5,
            borderTop: '1px solid',
            borderColor: colors.gray[500],
          }}
        >
          <Typography variant='caption' sx={{ fontWeight: 600, display: 'block' }}>
            Latest notes
          </Typography>
          {summary.latestNotes.map((note, idx) => (
            <Typography
              key={idx}
              variant='caption'
              sx={{ display: 'block', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}
            >
              • {note}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

interface BdUserLinkProps {
  id: number;
  summary?: BdUserSummary | null;
}

function BdUserLink({ id, summary }: BdUserLinkProps) {
  const link = (
    <Link to={`/users/lookup?id=${id}`} style={{ textDecoration: 'none' }}>
      <Typography variant='body2' sx={{ color: colors.ocean[500] }}>
        #{id}
      </Typography>
    </Link>
  );

  if (!summary) return link;

  return (
    <Tooltip title={<BdUserTooltipContent summary={summary} />} arrow placement='top'>
      <span>{link}</span>
    </Tooltip>
  );
}

export default BdUserLink;
